/**
 * Быстрая задача из календаря.
 *
 * Открывается щелчком по дню: срок уже проставлен, остаётся название и, по
 * желанию, ответственный. Полная форма со всеми полями типа доступна из
 * карточки — здесь нужно записать мысль, пока она не забылась.
 *
 * Тип фиксирован — задача. В календаре человек планирует работу, а не заводит
 * реестр расходов; выбор из пятнадцати типов здесь был бы лишним шагом.
 */

import { Loader2Icon, PlusIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { UserDto } from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useQuickAddItem, useUpdateItem } from '@/api/items';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserAvatar } from '@/components/common/user-avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDate } from '@/lib/format';
import { memberColorStyle } from '@/lib/member-color';

const NOBODY = '__nobody__';

export function QuickTaskDialog({
  projectId,
  date,
  members,
  onClose,
  onCreated,
}: {
  projectId: string;
  /** День, по которому щёлкнули. null — окно закрыто. */
  date: string | null;
  members: UserDto[];
  onClose: () => void;
  onCreated?: (itemId: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [ownerId, setOwnerId] = useState(NOBODY);
  const [dueDate, setDueDate] = useState('');

  const quickAdd = useQuickAddItem(projectId);
  const update = useUpdateItem(projectId);

  // Срок подставляется из выбранного дня, но остаётся редактируемым:
  // человек мог промахнуться мимо нужной клетки.
  useEffect(() => {
    if (date) {
      setDueDate(date);
      setTitle('');
      setOwnerId(NOBODY);
    }
  }, [date]);

  const submit = async () => {
    const name = title.trim();
    if (!name) return;

    try {
      // Быстрое добавление принимает только тип, название и ответственного:
      // срок ставится следующим шагом той же операцией, что и в форме.
      const created = await quickAdd.mutateAsync({
        type: 'TASK',
        title: name,
        ownerId: ownerId === NOBODY ? null : ownerId,
        properties: {},
      });

      if (dueDate) {
        await update.mutateAsync({ itemId: created.id, dueDate });
      }

      toast.success(`Задача на ${formatDate(dueDate || date!)}`);
      onCreated?.(created.id);
      onClose();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось создать задачу');
    }
  };

  const busy = quickAdd.isPending || update.isPending;

  return (
    <Dialog open={date !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Задача на {formatDate(date)}</DialogTitle>
          <DialogDescription>
            Обязательно только название. Остальное дописывается в карточке.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="quick-task-title">Что сделать</Label>
            <Input
              id="quick-task-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && title.trim()) {
                  event.preventDefault();
                  void submit();
                }
              }}
              placeholder="Например: созвониться с юристом"
              autoFocus
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="quick-task-owner">Ответственный</Label>
              <Select value={ownerId} onValueChange={setOwnerId}>
                <SelectTrigger id="quick-task-owner" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NOBODY}>Не назначен</SelectItem>
                  {members.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      <span className="flex items-center gap-1.5">
                        <span
                          className="member-color size-2 shrink-0 rounded-full"
                          style={memberColorStyle(member.id)}
                          aria-hidden
                        />
                        {member.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="quick-task-date">Срок</Label>
              <Input
                id="quick-task-date"
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
              />
            </div>
          </div>

          {ownerId !== NOBODY ? (
            <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
              <UserAvatar user={members.find((member) => member.id === ownerId)} size="xs" />В
              календаре задача будет окрашена цветом ответственного.
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button disabled={!title.trim() || busy} onClick={() => void submit()}>
            {busy ? <Loader2Icon className="animate-spin" /> : <PlusIcon />}
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
